import React from 'react'

export const useEnhancedInfoWindow = (
  shouldEnhance: boolean,
  enhancementKey: unknown
) => {
  const infoWindowRef = React.useRef<google.maps.InfoWindow | null>(null)
  const domListenerRef = React.useRef<google.maps.MapsEventListener | null>(
    null
  )

  const applyEnhancements = React.useCallback(() => {
    void enhancementKey

    const infoWindow = infoWindowRef.current
    if (!infoWindow) {
      return
    }

    const contentNode = infoWindow.getContent() as HTMLElement | null
    if (!contentNode) {
      return
    }

    const container = contentNode.parentElement
    if (!container) {
      return
    }

    // Find the gm-style-iw-c container (the one that needs background: inherit and box-shadow: none)
    const infoWindowContainer = container.closest(
      '.gm-style-iw-c'
    ) as HTMLElement | null

    const background = container.previousElementSibling as HTMLElement | null

    if (shouldEnhance) {
      // Apply styles to the InfoWindow container (gm-style-iw-c)
      if (infoWindowContainer) {
        infoWindowContainer.style.background = 'inherit'
        infoWindowContainer.style.boxShadow = 'none'
      }

      // Keep existing container styles
      container.style.padding = '0'
      container.style.borderRadius = '16px'
      container.style.overflow = 'visible'
      if (background) {
        background.style.display = 'none'
      }
    } else {
      // Remove styles from InfoWindow container
      if (infoWindowContainer) {
        infoWindowContainer.style.removeProperty('background')
        infoWindowContainer.style.removeProperty('boxShadow')
      }

      container.style.removeProperty('padding')
      container.style.removeProperty('borderRadius')
      container.style.removeProperty('overflow')
      if (background) {
        background.style.removeProperty('display')
      }
    }

    const closeButton = container.parentElement?.querySelector(
      'button[aria-label="Close"]'
    ) as HTMLElement | null

    if (closeButton) {
      if (shouldEnhance) {
        closeButton.style.display = 'none'
        closeButton.style.pointerEvents = 'none'
      } else {
        closeButton.style.removeProperty('display')
        closeButton.style.removeProperty('pointerEvents')
      }
    }
  }, [enhancementKey, shouldEnhance])

  const registerDomReady = React.useCallback(() => {
    const infoWindow = infoWindowRef.current
    if (!infoWindow) {
      return
    }

    if (domListenerRef.current) {
      google.maps.event.removeListener(domListenerRef.current)
      domListenerRef.current = null
    }

    domListenerRef.current = google.maps.event.addListenerOnce(
      infoWindow,
      'domready',
      () => {
        applyEnhancements()
        domListenerRef.current = null
      }
    )
  }, [applyEnhancements])

  React.useEffect(() => {
    registerDomReady()

    return () => {
      if (domListenerRef.current) {
        google.maps.event.removeListener(domListenerRef.current)
        domListenerRef.current = null
      }
    }
  }, [registerDomReady])

  const handleInfoWindowLoad = React.useCallback(
    (infoWindow: google.maps.InfoWindow) => {
      infoWindowRef.current = infoWindow
      registerDomReady()
    },
    [registerDomReady]
  )

  return { handleInfoWindowLoad }
}
